import { Badge, type BadgeProps } from '@proerp/ui';
import {
  PURCHASE_ALLOWED_CONVERSIONS,
  PURCHASE_ALLOWED_STATUSES_BY_KIND,
  PURCHASE_SYSTEM_STATUSES,
  type PurchaseDocumentKind,
  type PurchaseOrderStatus,
} from '@proerp/shared';

const KIND_LABELS: Record<PurchaseDocumentKind, string> = {
  ANFRAGE: 'Anfrage',
  BESTELLUNG: 'Bestellung',
  EINGANGSRECHNUNG: 'Eingangsrechnung',
};

export function purchaseKindLabel(kind: PurchaseDocumentKind): string {
  return KIND_LABELS[kind];
}

const KIND_LABELS_EN: Record<PurchaseDocumentKind, string> = {
  ANFRAGE: 'Request for Quotation',
  BESTELLUNG: 'Purchase Order',
  EINGANGSRECHNUNG: 'Incoming Invoice',
};

/**
 * Betreff/Titel einer Mail zum Einkaufsbeleg in der Belegsprache — „Purchase Order
 * BE-…" für eine englische Bestellung (wie der Titel im englischen PDF).
 */
export function purchaseMailTitle(order: { kind: PurchaseDocumentKind; number: string; language: 'DE' | 'EN' }): string {
  return `${order.language === 'EN' ? KIND_LABELS_EN[order.kind] : KIND_LABELS[order.kind]} ${order.number}`;
}

/** Tabs/Filter der Einkaufs-Belegliste (Belegfluss Anfrage → Bestellung → Eingangsrechnung). */
export const PURCHASE_KIND_TABS: { kind: PurchaseDocumentKind; label: string }[] = [
  { kind: 'ANFRAGE', label: 'Anfragen' },
  { kind: 'BESTELLUNG', label: 'Bestellungen' },
  { kind: 'EINGANGSRECHNUNG', label: 'Eingangsrechnungen' },
];

const STATUS_META: Record<PurchaseOrderStatus, { tone: BadgeProps['tone']; label: string }> = {
  POOL: { tone: 'warning', label: 'Im Pool' },
  DRAFT: { tone: 'neutral', label: 'Entwurf' },
  ORDERED: { tone: 'info', label: 'Bestellt' },
  RECEIVED: { tone: 'success', label: 'Geliefert' },
  CANCELLED: { tone: 'neutral', label: 'Storniert' },
};

// „ORDERED" bedeutet je nach Belegart etwas anderes (Bestellt/Angefragt/Gebucht).
const STATUS_LABEL_BY_KIND: Partial<
  Record<PurchaseDocumentKind, Partial<Record<PurchaseOrderStatus, string>>>
> = {
  ANFRAGE: { ORDERED: 'Angefragt' },
  EINGANGSRECHNUNG: { ORDERED: 'Gebucht' },
};

export function purchaseStatusLabel(status: PurchaseOrderStatus, kind?: PurchaseDocumentKind): string {
  return (kind && STATUS_LABEL_BY_KIND[kind]?.[status]) ?? STATUS_META[status].label;
}

/** Basis-Statusoptionen (kind-unabhängig) — für den einfachen Listenfilter. */
export const PURCHASE_STATUS_OPTIONS = (Object.keys(STATUS_META) as PurchaseOrderStatus[]).map(
  (value) => ({ value, label: STATUS_META[value].label }),
);

/**
 * Status-Menü je Belegart (Spiegel von PURCHASE_ALLOWED_STATUSES_BY_KIND). „Geliefert"
 * setzt nur der Wareneingang (PURCHASE_SYSTEM_STATUSES, Befund 2026-09-28): es
 * erscheint nur, wenn der Beleg schon so steht — dann als `isSystem` (nicht wählbar).
 */
export function purchaseStatusOptionsForKind(
  kind: PurchaseDocumentKind,
  currentStatus?: PurchaseOrderStatus,
): { value: PurchaseOrderStatus; label: string; isSystem: boolean }[] {
  return PURCHASE_ALLOWED_STATUSES_BY_KIND[kind]
    .filter((value) => !PURCHASE_SYSTEM_STATUSES.includes(value) || value === currentStatus)
    .map((value) => ({
      value,
      label: purchaseStatusLabel(value, kind),
      isSystem: PURCHASE_SYSTEM_STATUSES.includes(value),
    }));
}

/** Erlaubte Umwandlungsziele je Belegart (Spiegel des Backends). */
export function purchaseConvertTargets(
  kind: PurchaseDocumentKind,
): { kind: PurchaseDocumentKind; label: string }[] {
  return PURCHASE_ALLOWED_CONVERSIONS[kind].map((target) => ({
    kind: target,
    label: `→ ${KIND_LABELS[target]}`,
  }));
}

export function PurchaseStatusBadge({
  status,
  kind,
}: {
  status: PurchaseOrderStatus;
  kind?: PurchaseDocumentKind;
}) {
  const { tone } = STATUS_META[status];
  return <Badge tone={tone}>{purchaseStatusLabel(status, kind)}</Badge>;
}
